import { logger } from '@celito.clients/services';
import { clearLoginAttempts } from 'libs/core/src/config/login-loop-guard';
import React from 'react';

import { BootErrorView } from '../screens/boot-error';

/**
 * BootErrorBoundary (ADO #38557).
 *
 * A top-level React error boundary wrapped around the entire boot/auth root
 * (`<Main />` in main.tsx). ANY render or auth error thrown during boot — most
 * importantly an MSAL token-acquisition failure — is caught here and rendered
 * as an explicit {@link BootErrorView} instead of crashing to a blank page.
 *
 * It sits ABOVE the MsalProvider/Router, so it also catches failures that the
 * in-app `withErrorBoundary` on `App` (which lives below the router) cannot.
 *
 * Retry clears the login-loop guard and performs a hard reload, giving the
 * user a single explicit fresh attempt without re-entering an automatic loop.
 */

interface BootErrorBoundaryProps {
  children: React.ReactNode;
}

interface BootErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class BootErrorBoundary extends React.Component<
  BootErrorBoundaryProps,
  BootErrorBoundaryState
> {
  constructor(props: BootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error: Error): BootErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error(error, { origin: 'BootErrorBoundary', ...info });
  }

  handleRetry(): void {
    // Reset the automatic-redirect budget so the reload gets a clean attempt,
    // then hard-reload to re-run the (bounded) boot sequence.
    clearLoginAttempts();
    window.location.reload();
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <BootErrorView error={this.state.error} onRetry={this.handleRetry} />
      );
    }

    return this.props.children;
  }
}

export default BootErrorBoundary;
